import {createAction} from 'redux-actions'
import {
  avatarsRpc,
  AVATAR_READ_METADATA,
  AVATAR_RESET_AVATAR,
  AVATAR_DELETE,
  AVATAR_UPLOAD_AVATAR,
  AVATAR_SET_ACTIVE
} from '@rambler-id/transport'
import {trackEvent} from 'utils/metrics'
import {sentryReact as Sentry} from '@rambler-id/sentry'
import {throwError} from 'containers/App/actions'
import {selectInfo, selectProfileType} from 'models/user/selectors'

export const updateAvatars = createAction('UPDATE_AVATARS')
export const addAvatar = createAction('ADD_AVATAR')
export const cancelAvatar = createAction('CANCEL_AVATAR')
export const updateActiveAvatar = createAction('UPDATE_ACTIVE_AVATAR')

export const loadAvatars = () => async (dispatch) => {
  try {
    const response = await avatarsRpc(AVATAR_READ_METADATA)

    dispatch(updateAvatars(response.metadata.avatars))
  } catch (error) {
    if (error.code === 0) {
      dispatch(
        throwError(
          'По техническим причинам отображены не все данные. При необходимости перезагрузите страницу.'
        )
      )

      return
    }

    if (error.message !== 'metadata not found') {
      Sentry.captureException(error)
    }
  }
}

export const uploadAvatar = (imageFile) => async (dispatch, getState) => {
  if (imageFile == null || !/^image\/(jpeg|png|bmp)$/.test(imageFile.type)) {
    dispatch(
      throwError(
        'Для загрузки допускаются только изображения в форматах .jpg, .png, .bmp'
      )
    )

    return
  }

  if (imageFile.size > 1048576) {
    trackEvent('avatars', {size_limit_exceeded: true})
    dispatch(
      throwError('Фото не загружено. Размер картинки не может превышать 1 MB')
    )

    return
  }

  const info = selectInfo(getState())
  const type = selectProfileType(getState())
  const data = new FormData()

  data.append('image', imageFile)

  try {
    const {id} = await avatarsRpc(AVATAR_UPLOAD_AVATAR, data)
    let baseUrl = type === 'general' ? info.email : info.chainId.default

    dispatch(
      addAvatar({
        id,
        key: `/${baseUrl}/private/${id}`
      })
    )

    await avatarsRpc(AVATAR_SET_ACTIVE, {id})
    dispatch(updateActiveAvatar(id))
    trackEvent('avatars', {upload_success: true})
  } catch (error) {
    if (error.code === -8 && error.message === 'only image/* are accepted') {
      dispatch(
        throwError(
          'Для загрузки допускаются только изображения в форматах .jpg, .png, .bmp'
        )
      )

      return
    }

    if (error.code === -6 && error.message === 'file already exists') {
      dispatch(throwError('Это фото уже загружено'))

      return
    }

    Sentry.captureException(error)
    dispatch(throwError('Не удалось загрузить фото'))
  }
}

export const deleteAvatar = (id) => async (dispatch) => {
  try {
    await avatarsRpc(AVATAR_DELETE, {id})
    dispatch(cancelAvatar(id))
  } catch (error) {
    Sentry.captureException(error)
    dispatch(throwError('Не удалось удалить фото'))
  }
}

export const activateAvatar = (id) => async (dispatch) => {
  try {
    await avatarsRpc(AVATAR_SET_ACTIVE, {id})
    dispatch(updateActiveAvatar(id))
    trackEvent('avatars', {change_success: true})
  } catch (error) {
    Sentry.captureException(error)
    dispatch(throwError('Не удалось обновить фото'))
  }
}

export const resetAvatar = (id) => async (dispatch) => {
  try {
    await avatarsRpc(AVATAR_RESET_AVATAR, {id})
    dispatch(updateActiveAvatar(null))
  } catch (error) {
    Sentry.captureException(error)
    dispatch(throwError('Не удалось установить стандартный аватар'))
  }
}
